import { useCallback, useState, type HTMLAttributes } from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  Button,
  Icon,
  ImageryCredit,
  LinkButton,
  Menu,
  SiteImage,
  StageTrack,
  StatusDot,
  PIPELINE_ORDER,
  statusDef,
  useContextMenu,
  useToast,
  type ContextMenuEntry,
} from '@/components/ui';
import { ACTIVE_STAGES, countryName, locationPlace, type Location } from './api';
import { HandoffDialog, useDeleteSite, type LocationRef, type useStageMove } from './commands';

type Move = ReturnType<typeof useStageMove>;

export const locationRef = (l: Pick<Location, 'id' | 'version' | 'name' | 'pipeline_state'>): LocationRef => ({
  id: l.id,
  version: l.version,
  name: l.name,
  pipeline_state: l.pipeline_state,
});

/** "Kaai 730, 2030 Antwerpen, Belgium". */
export function fullAddress(l: Location): string {
  const a = l.address;
  const city = [a.postal_code, a.locality].filter(Boolean).join(' ');
  return [a.address_line1, a.address_line2, city, a.region, a.country_code ? countryName(a.country_code) : null].filter(Boolean).join(', ');
}

/** Owning company: the primary link, else the owner. */
export function ownerOf(l: Location) {
  return l.companies.find((c) => c.is_primary) ?? l.companies.find((c) => c.role === 'owner') ?? null;
}

/** Satellite view with the site name set over a soft scrim. The heading level follows the context. */
export function SiteHero({
  location: l,
  height,
  zoom = 17,
  as: H = 'h2',
  headingProps,
}: {
  location: Location;
  height: number;
  zoom?: number;
  as?: 'h1' | 'h2' | 'p';
  headingProps?: HTMLAttributes<HTMLElement> & { 'data-page-heading'?: boolean };
}) {
  const place = locationPlace({ locality: l.address.locality, country_code: l.address.country_code });
  return (
    <header className="site-hero" style={{ height }}>
      <SiteImage
        lat={l.address.latitude}
        lon={l.address.longitude}
        zoom={zoom}
        width="100%"
        height={height}
        label={`Satellite view of ${l.name}`}
        city={l.address.locality}
        credit={false}
        className="site-hero-image"
      />
      <div className="site-hero-text">
        {place ? <p className="site-hero-place">{place}</p> : null}
        <H className="site-hero-name display" tabIndex={-1} {...headingProps}>
          {l.name}
        </H>
      </div>
      {l.address.latitude == null || l.address.longitude == null ? (
        l.deleted_at ? null : (
          <LinkButton to="/app/board/$locationId/edit" params={{ locationId: l.id }} size="sm" icon="pin" className="site-hero-add">
            Add coordinates
          </LinkButton>
        )
      ) : null}
      <ImageryCredit className="site-hero-credit" />
    </header>
  );
}

/** Current stage as a menu button over its track; moves go through the guarded transition. */
export function StageControl({ location: l, move }: { location: Location; move: Move }) {
  const stage = statusDef('pipeline', l.pipeline_state).label;
  const disabled = Boolean(l.deleted_at);
  const at = (ACTIVE_STAGES as readonly string[]).indexOf(l.pipeline_state);
  const step = at >= 0 ? `${at + 1} of ${ACTIVE_STAGES.length}` : 'Closed';
  return (
    <div className="stage-control">
      {disabled ? (
        <span className="stage-control-btn" data-static>
          {stage}
        </span>
      ) : (
        <Menu
          label={`Stage: ${stage}`}
          align="left"
          trigger={
            <span className="stage-control-btn">
              {move.pending ? <span className="faint">Moving to {statusDef('pipeline', move.pending.to).label}</span> : <span>{stage}</span>}
              <Icon name="chevronDown" size={14} />
            </span>
          }
          items={[
            { heading: 'Move to' },
            ...PIPELINE_ORDER.map((s) => ({
              label: statusDef('pipeline', s).label,
              checked: s === l.pipeline_state,
              disabled: s === l.pipeline_state || Boolean(move.pending),
              onSelect: () => move.request(locationRef(l), s),
            })),
          ]}
        />
      )}
      <span className="stage-control-track">
        <StageTrack stage={l.pipeline_state} label={false} />
        <span className="stage-control-step num" aria-hidden>
          {step}
        </span>
      </span>
    </div>
  );
}

/** Handoff and red flag notes under the site name. */
export function SiteNotices({ location: l }: { location: Location }) {
  const manualOnly = l.policy.handoff_state === 'manual_only';
  const flags = l.red_flags.filter((f) => f.state === 'active');
  if (!manualOnly && !flags.length) return null;
  return (
    <p className="site-notices">
      {manualOnly ? <StatusDot tone="neutral">Handed off</StatusDot> : null}
      {flags.map((f) => (
        <StatusDot key={f.id} tone="accent" title={f.reason}>
          {f.label}
        </StatusDot>
      ))}
    </p>
  );
}

/** Copies the site's full page address. */
export function useCopySiteLink() {
  const toast = useToast();
  return useCallback(
    async (id: string) => {
      try {
        await navigator.clipboard.writeText(new URL(`/app/board/${encodeURIComponent(id)}`, window.location.origin).href);
        toast.show({ title: 'Link copied' });
      } catch {
        toast.show({ tone: 'accent', title: 'Link not copied' });
      }
    },
    [toast],
  );
}

/** Edit, hand off, copy link, archive, delete. `onDeleted` runs as the site is deleted (e.g. closing the panel). */
export function SiteMenu({ location: l, move, onDeleted }: { location: Location; move: Move; onDeleted?: () => void }) {
  const navigate = useNavigate();
  const menu = useContextMenu();
  const copyLink = useCopySiteLink();
  const remove = useDeleteSite();
  const [handoff, setHandoff] = useState(false);
  const manualOnly = l.policy.handoff_state === 'manual_only';
  const archived = l.pipeline_state === 'archived';
  if (l.deleted_at) return null;
  const items = (): ContextMenuEntry[] => [
    { label: 'Edit details', onSelect: () => void navigate({ to: '/app/board/$locationId/edit', params: { locationId: l.id } }) },
    { label: 'Hand off', disabled: manualOnly, hint: manualOnly ? 'Handed off' : undefined, onSelect: () => setHandoff(true) },
    { label: 'Copy link', onSelect: () => void copyLink(l.id) },
    {
      label: 'Archive site',
      danger: true,
      disabled: archived,
      hint: archived ? 'Archived' : undefined,
      onSelect: () => move.request(locationRef(l), 'archived'),
    },
    {
      label: 'Delete site',
      danger: true,
      onSelect: () => {
        void remove(locationRef(l));
        onDeleted?.();
      },
    },
  ];
  return (
    <>
      <Button
        variant="quiet"
        iconOnly
        icon="more"
        aria-label="Site actions"
        aria-expanded={menu.openKey === l.id}
        {...menu.trigger(l.id, 'Site actions', items)}
      />
      {menu.element}
      <HandoffDialog location={locationRef(l)} open={handoff} onClose={() => setHandoff(false)} />
    </>
  );
}
